import React, { useEffect, useState } from 'react';
import { QueryClient } from 'react-query';

import checkIcon from 'images/white-check-icon.svg';

import { useFollowUpdate } from '../../../hooks';
import { FollowAction, FollowType } from '../../../utils/backend-api/follow';
import { Tooltip } from '../../BaseUI';

export type FollowButtonProps = {
  followStatus: boolean;
  followeeId: number;
  followType: FollowType;
  showSubscribeModal?: boolean;
  username?: string;
  queryClient: QueryClient;
  name: string;
  tooltipEnabled?: boolean;
  evolvingStoryTooltip?: boolean;
};

const defaultTooltipMessage = (isFollowing, name) =>
  isFollowing
    ? `You’re now following ${name}! You’ll receive emails when they update their profile, comment on articles and post in the Forum.`
    : `Get notifications when ${name} is active on The Information.`;

const EVOLVING_STORY_TOOLTIP_COPY = 'Use the "follow" button to watch how this story develops.';

const FollowButton = ({
  followStatus,
  followeeId,
  followType,
  showSubscribeModal = false,
  username,
  queryClient,
  name,
  tooltipEnabled = false,
  evolvingStoryTooltip,
}: FollowButtonProps) => {
  const [text, setText] = useState(followStatus ? 'Following' : 'Follow');
  const [isFollowing, setIsFollowing] = useState(followStatus);
  const [showFollowTooltip, setShowFollowTooltip] = useState(false);
  const { mutateAsync: updateFollow } = useFollowUpdate();
  const showEvolvingStoryTooltip = !evolvingStoryTooltip || !isFollowing;

  useEffect(() => {
    setIsFollowing(followStatus);
    setText(followStatus ? 'Following' : 'Follow');
  }, [followStatus]);

  const handleFollow = async () => {
    if (showSubscribeModal) {
      handleLaunchModal();
      return;
    }

    updateFollow(
      { followeeId, followAction: FollowAction.Follow, followType, username, queryClient },
      {
        onSuccess: () => {
          setText('Following');
          setIsFollowing(true);
        },
      }
    );
  };

  const handleUnfollow = async () => {
    updateFollow(
      { followeeId, followAction: FollowAction.Unfollow, followType, username, queryClient },
      {
        onSuccess: () => {
          setText('Follow');
          setIsFollowing(false);
        },
      }
    );
  };

  const handleLaunchModal = () => {
    const immersiveArticleModalContainer = document.getElementById(
      'immersive-article-modal-container'
    );
    immersiveArticleModalContainer.classList.remove('hidden');

    // Prevent clicks inside the modal from bubbling up to the modal container
    const immersiveArticleModal = document.getElementById('immersive-article-modal');
    immersiveArticleModal.addEventListener('click', (e) => e.stopPropagation());

    const modalcloseBtn = document.getElementById('immersive-article-modal-close-btn-container');
    [immersiveArticleModalContainer, modalcloseBtn].forEach((elt) => {
      elt.addEventListener('click', () => immersiveArticleModalContainer.classList.add('hidden'));
    });
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        className={isFollowing ? 'FollowButton --following' : 'FollowButton'}
        onClick={isFollowing ? handleUnfollow : handleFollow}
        onMouseEnter={() => {
          if (isFollowing) setText('Unfollow');
          setShowFollowTooltip(true);
        }}
        onMouseLeave={() => {
          if (isFollowing) setText('Following');
          setShowFollowTooltip(false);
        }}
        data-test="follow-button"
      >
        {isFollowing && (
          <img width="15" height="14" src={checkIcon} className="check-circle" alt="check-circle" />
        )}
        <span>{text}</span>
      </button>
      <Tooltip
        message={
          evolvingStoryTooltip
            ? EVOLVING_STORY_TOOLTIP_COPY
            : defaultTooltipMessage(isFollowing, name)
        }
        showTooltip={tooltipEnabled && showFollowTooltip && showEvolvingStoryTooltip}
      />
    </div>
  );
};

export default FollowButton;
